import { useState, useRef, useEffect } from 'react'
import * as Collapsible from '@radix-ui/react-collapsible'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ChevronDown, GripVertical, Loader2 } from 'lucide-react'
import { useDraggable } from '@dnd-kit/core'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import {
  EDITORIAL_GRID_PRESET_CONFIG,
  type EditorialGridLayoutPreset,
} from '../page-blocks/definitions/editorialGrid'
import { PresetVisual } from './PresetVisual'
import { useAdminArticles, useArticleSearch } from '../../api/hooks'
import type { BlockCanvasState } from './useBlockCanvas'
import type { PageBlockType, WireArticle } from '../../api/types'

// ─── Draggable shelf button ───────────────────────────────────────────────────
// Each instance generates a stable UUID on mount so duplicate block types
// never share an ID — the UUID is used directly as the final slot ID on drop.

function DraggableShelfButton({
  type,
  className,
  children,
}: {
  type: PageBlockType
  className: string
  children: React.ReactNode
}) {
  const [id, setId] = useState(() => crypto.randomUUID())
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { kind: 'block', type },
  })
  // Regenerate UUID after each drag so the next drag doesn't collide with the
  // slot that was just inserted on the canvas.
  const wasDragging = useRef(false)
  useEffect(() => {
    if (wasDragging.current && !isDragging) setId(crypto.randomUUID())
    wasDragging.current = isDragging
  }, [isDragging])
  return (
    <button
      ref={setNodeRef}
      type="button"
      className={`${className} cursor-grab touch-none active:cursor-grabbing`}
      style={{ opacity: isDragging ? 0.4 : undefined }}
      {...attributes}
      {...listeners}
    >
      {children}
    </button>
  )
}

// ─── Compact editorial-grid preset item ────────────────────────────────────────────────

function DraggablePresetButton({ preset }: { preset: EditorialGridLayoutPreset }) {
  const [id, setId] = useState(() => crypto.randomUUID())
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { kind: 'hrow', preset },
  })
  const wasDragging = useRef(false)
  useEffect(() => {
    if (wasDragging.current && !isDragging) setId(crypto.randomUUID())
    wasDragging.current = isDragging
  }, [isDragging])
  const cfg = EDITORIAL_GRID_PRESET_CONFIG[preset]
  return (
    <button
      ref={setNodeRef}
      type="button"
      className="flex w-full cursor-grab items-center gap-2 rounded border border-line bg-canvas px-2 py-1.5 text-left text-xs touch-none transition-colors hover:bg-fill active:cursor-grabbing"
      style={{ opacity: isDragging ? 0.4 : undefined }}
      {...attributes}
      {...listeners}
    >
      <div className="w-16 shrink-0">
        <PresetVisual gridTemplate={cfg.gridTemplate} />
      </div>
      <span className="font-medium text-fg leading-tight">{cfg.label}</span>
    </button>
  )
}

// ─── Shelf section wrapper ────────────────────────────────────────────────────
// Option A: header-bar treatment with border + tint makes collapsibility clear.
// Option B: count badge on the right shows "there are things inside".

function ShelfSection({
  label,
  count,
  defaultOpen = true,
  children,
}: {
  label: string
  count: number
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  return (
    <Collapsible.Root defaultOpen={defaultOpen} className="mb-1">
      <Collapsible.Trigger className="group -mx-2 flex w-[calc(100%+1rem)] items-center gap-1.5 rounded border border-transparent px-2 py-1.5 transition-colors hover:border-line hover:bg-fill data-[state=open]:bg-fill/60">
        <ChevronDown
          size={11}
          className="shrink-0 text-accent-tertiary-fg transition-transform duration-150 group-data-[state=closed]:-rotate-90"
        />
        <span className="type-section-label flex-1 text-left text-accent-tertiary-fg group-hover:text-fg">
          {label}
        </span>
        <span className="type-badge rounded-full bg-line px-1.5 py-0.5 tabular-nums text-fg-secondary">
          {count}
        </span>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <div className="mb-3 mt-1 space-y-1 border-l-2 border-line pl-2">
          {children}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}

// ─── Draggable article item ─────────────────────────────────────────────────

function DraggableArticleItem({ article, isUsed = false }: { article: WireArticle; isUsed?: boolean }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `article-shelf:${article.id}`,
    data: { kind: 'article', articleId: article.id, title: article.title },
  })
  const clf = article.classification[0] ?? ''
  const metaParts = [clf && clf.toUpperCase(), ...(article.regions ?? []), ...(article.topics ?? [])].filter(Boolean)
  return (
    <div
      ref={setNodeRef}
      className={[
        'flex cursor-grab touch-none items-start gap-1.5 rounded border px-2 py-1 transition-colors active:cursor-grabbing',
        isUsed
          ? 'border-line/50 bg-gradient-to-br from-canvas to-jade-2/50 dark:from-fill dark:to-jade-3/30 opacity-50'
          : 'border-jade-6/40 dark:border-jade-7/30 bg-gradient-to-br from-canvas to-jade-3 dark:from-fill dark:to-jade-3/40 hover:to-jade-4 dark:hover:to-jade-3/60',
      ].join(' ')}
      style={{ opacity: isDragging ? 0.4 : undefined }}
      {...attributes}
      {...listeners}
    >
      <GripVertical size={10} className="mt-[3px] shrink-0 text-fg-secondary opacity-30" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-[11px] font-semibold leading-snug text-fg">{article.title}</p>
        <div className="mt-0.5 flex items-center gap-1.5">
          {metaParts.length > 0 && (
            <span className="font-mono text-[9px] text-fg-secondary opacity-60">
              {metaParts.join(' · ')}
            </span>
          )}
          {isUsed && (
            <span className="rounded bg-fill px-1 py-px text-[8px] font-bold uppercase tracking-wide text-fg-secondary">
              in page
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Articles panel ───────────────────────────────────────────────────────────

type ArticleSort = 'alpha' | 'modified' | 'region' | 'topic'

const SORT_OPTIONS: { value: ArticleSort; label: string }[] = [
  { value: 'alpha',    label: 'A–Z'      },
  { value: 'modified', label: 'Modified' },
  { value: 'region',   label: 'Region'   },
  { value: 'topic',    label: 'Topic'    },
]

function sortArticles(articles: WireArticle[], sort: ArticleSort): WireArticle[] {
  const copy = [...articles]
  if (sort === 'alpha') {
    return copy.sort((a, b) => a.title.localeCompare(b.title))
  }
  if (sort === 'modified') {
    return copy.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
  }
  if (sort === 'topic') {
    return copy.sort((a, b) => {
      const ta = a.topics?.[0] ?? ''
      const tb = b.topics?.[0] ?? ''
      return ta !== tb ? ta.localeCompare(tb) : a.title.localeCompare(b.title)
    })
  }
  // region: sort by first region (empty last), then alpha within region
  return copy.sort((a, b) => {
    const ra = a.regions?.[0] ?? ''
    const rb = b.regions?.[0] ?? ''
    return ra !== rb ? ra.localeCompare(rb) : a.title.localeCompare(b.title)
  })
}

function ArticlesPanel({ usedArticleIds }: { usedArticleIds: Set<string> }) {
  const { data: articles = [], isLoading } = useAdminArticles()
  const [sort, setSort] = useState<ArticleSort>('alpha')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={16} className="animate-spin text-fg-secondary" />
      </div>
    )
  }

  if (articles.length === 0) {
    return (
      <p className="py-4 text-center text-[11px] text-fg-secondary">No articles found.</p>
    )
  }

  const sorted = sortArticles(articles, sort)

  return (
    <div>
      {/* Sort dropdown */}
      <div className="mb-2">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded border border-line bg-canvas px-2 py-1.5 text-[11px] font-semibold text-fg transition-colors hover:border-line-strong"
            >
              <span className="text-fg-secondary">Sort: </span>
              <span className="ml-1 text-fg">{SORT_OPTIONS.find(o => o.value === sort)?.label}</span>
              <ChevronDown size={11} className="ml-auto shrink-0 text-fg-secondary" aria-hidden="true" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              sideOffset={4}
              align="start"
              className="z-50 w-[var(--radix-dropdown-menu-trigger-width)] rounded border border-line bg-canvas py-1 shadow-lg"
            >
              {SORT_OPTIONS.map(({ value, label }) => (
                <DropdownMenu.Item
                  key={value}
                  onSelect={() => setSort(value)}
                  className={[
                    'flex cursor-default items-center px-3 py-1.5 text-[11px] outline-none transition-colors hover:bg-fill focus:bg-fill',
                    sort === value ? 'font-semibold text-fg' : 'text-fg-secondary',
                  ].join(' ')}
                >
                  {label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      {/* Article list */}
      {sort === 'region' ? (
        (() => {
          const groups = new Map<string, WireArticle[]>()
          for (const a of sorted) {
            const keys = a.regions && a.regions.length > 0 ? a.regions : ['Other']
            for (const key of keys) {
              const g = groups.get(key) ?? []
              g.push(a)
              groups.set(key, g)
            }
          }
          return Array.from(groups.entries()).map(([key, items]) => (
            <div key={key} className="mb-3">
              <div className="mb-1 px-0.5 text-[9px] font-bold uppercase tracking-wider text-fg-secondary opacity-70">
                {key}
              </div>
              <div className="space-y-1">
                {items.map((article) => (
                  <DraggableArticleItem key={article.id} article={article} isUsed={usedArticleIds.has(article.id)} />
                ))}
              </div>
            </div>
          ))
        })()
      ) : sort === 'topic' ? (
        (() => {
          const groups = new Map<string, WireArticle[]>()
          for (const a of sorted) {
            const keys = a.topics && a.topics.length > 0 ? a.topics : ['Other']
            for (const key of keys) {
              const g = groups.get(key) ?? []
              g.push(a)
              groups.set(key, g)
            }
          }
          return Array.from(groups.entries()).map(([key, items]) => (
            <div key={key} className="mb-3">
              <div className="mb-1 px-0.5 text-[9px] font-bold uppercase tracking-wider text-fg-secondary opacity-70">
                {key}
              </div>
              <div className="space-y-1">
                {items.map((article) => (
                  <DraggableArticleItem key={article.id} article={article} isUsed={usedArticleIds.has(article.id)} />
                ))}
              </div>
            </div>
          ))
        })()
      ) : (
        <div className="space-y-1">
          {sorted.map((article) => (
            <DraggableArticleItem key={article.id} article={article} isUsed={usedArticleIds.has(article.id)} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Published articles panel ────────────────────────────────────────────────────

function PublishedArticlesPanel({ usedArticleIds }: { usedArticleIds: Set<string> }) {
  const [query, setQuery] = useState('')
  const { data: results = [], isFetching } = useArticleSearch(query)

  return (
    <div>
      <div className="relative mb-2">
        <input
          type="search"
          placeholder="Search published articles…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg placeholder:text-fg-secondary focus:border-accent-primary-solid focus:outline-none"
        />
        {isFetching && (
          <span
            aria-hidden="true"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-fg-secondary opacity-60"
          >
            …
          </span>
        )}
      </div>
      {query.trim().length < 2 ? (
        <p className="py-4 text-center text-[11px] text-fg-secondary">
          Type at least 2 characters to search.
        </p>
      ) : results.length === 0 && !isFetching ? (
        <p className="py-4 text-center text-[11px] italic text-fg-secondary">No published articles found.</p>
      ) : (
        <div className="space-y-1">
          {results.map((article) => (
            <DraggableArticleItem key={article.id} article={article} isUsed={usedArticleIds.has(article.id)} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── BlockPickerShelf ─────────────────────────────────────────────────────────

interface BlockPickerShelfProps {
  canvas: BlockCanvasState
  usedArticleIds?: Set<string>
  hideArticles?: boolean
  readOnly?: boolean
}

export function BlockPickerShelf({ canvas, usedArticleIds = new Set(), hideArticles = false, readOnly = false }: BlockPickerShelfProps) {
  const {
    pickerMode,
    editorialLeadTypes,
    editorialRowTypes,
    structuralTypes,
    columnCompatibleTypes,
    assignColumnBlock: _assignColumnBlock,
    addColBlock,
    setPickerMode,
  } = canvas

  const hrowPresets = Object.keys(EDITORIAL_GRID_PRESET_CONFIG) as EditorialGridLayoutPreset[]
  const [tab, setTab] = useState<'blocks' | 'drafts' | 'published'>(hideArticles ? 'blocks' : 'drafts')

  if (readOnly) {
    return (
      <aside className="w-64 shrink-0 border-r border-line bg-shelf flex items-center justify-center">
        <p className="px-6 text-center text-[11px] text-fg-secondary opacity-60">Shelf unavailable in read-only mode</p>
      </aside>
    )
  }

  return (
    <aside className="w-64 shrink-0 overflow-y-auto border-r border-line bg-shelf">

      {/* Tab switcher — hidden while in column-assign mode or when articles are disabled */}
      {pickerMode.kind === 'add' && !hideArticles && (
        <div className="border-b border-line px-3 pb-2 pt-2.5">
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-accent-tertiary-fg">Source</p>
          <select
            value={tab}
            onChange={(e) => setTab(e.target.value as 'blocks' | 'drafts' | 'published')}
            className="w-full rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg focus:border-accent-primary-solid focus:outline-none"
          >
            <option value="blocks">Blocks</option>
            <option value="drafts">Drafts</option>
            <option value="published">Published Articles</option>
          </select>
        </div>
      )}

      <div className="p-4">

      {pickerMode.kind === 'add' && tab === 'drafts' && (
        <ArticlesPanel usedArticleIds={usedArticleIds} />
      )}

      {pickerMode.kind === 'add' && tab === 'published' && (
        <PublishedArticlesPanel usedArticleIds={usedArticleIds} />
      )}

      {pickerMode.kind === 'add' && tab === 'blocks' && (
        <>
          {/* Editorial — Leads */}
          <ShelfSection label="Editorial — Leads" count={editorialLeadTypes.length}>
            {editorialLeadTypes.map((type) => {
              const def = PAGE_BLOCK_REGISTRY[type]
              return (
                <DraggableShelfButton
                  key={type}
                  type={type}
                  className="w-full rounded border border-line bg-canvas px-3 py-2 text-left text-xs transition-colors hover:bg-fill"
                >
                  <div className="font-semibold text-fg">{def.label}</div>
                  <div className="mt-0.5 text-fg-secondary">{def.description}</div>
                </DraggableShelfButton>
              )
            })}
          </ShelfSection>

          {/* Editorial — Rows */}
          <ShelfSection
            label="Editorial — Rows"
            count={editorialRowTypes.filter((t: PageBlockType) => t !== 'editorial-grid').length}
          >
            {editorialRowTypes
              .filter((t: PageBlockType) => t !== 'editorial-grid')
              .map((type: PageBlockType) => {
                const def = PAGE_BLOCK_REGISTRY[type]
                return (
                  <DraggableShelfButton
                    key={type}
                    type={type}
                    className="w-full rounded border border-line bg-canvas px-3 py-2 text-left text-xs transition-colors hover:bg-fill"
                  >
                    <div className="font-semibold text-fg">{def.label}</div>
                    <div className="mt-0.5 text-fg-secondary">{def.description}</div>
                  </DraggableShelfButton>
                )
              })}
          </ShelfSection>

          {/* Editorial Grid */}
          <ShelfSection label="Editorial Grid" count={hrowPresets.length}>
            {hrowPresets.map((preset) => (
              <DraggablePresetButton key={preset} preset={preset} />
            ))}
          </ShelfSection>

          {/* Structural */}
          <ShelfSection label="Structural" count={structuralTypes.length}>
            {structuralTypes.map((type) => {
              const def = PAGE_BLOCK_REGISTRY[type]
              return (
                <DraggableShelfButton
                  key={type}
                  type={type}
                  className="w-full rounded border border-dashed border-line bg-canvas px-3 py-2 text-left text-xs transition-colors hover:bg-fill"
                >
                  <div className="font-semibold text-fg">{def.label}</div>
                  <div className="mt-0.5 text-fg-secondary">{def.description}</div>
                </DraggableShelfButton>
              )
            })}
          </ShelfSection>
        </>
      )}

      </div>

      {pickerMode.kind === 'assign' && (
      <div className="p-4">
        <>
          <div className="mb-3 rounded border border-info-line bg-info-fill p-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wide text-info-fg">
                Assigning → {pickerMode.colLabel}
              </span>
              <button
                type="button"
                onClick={() => setPickerMode({ kind: 'add' })}
                className="rounded px-1 text-blue-11 hover:bg-blue-4"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="space-y-1">
            {columnCompatibleTypes.map((type) => {
              const def = PAGE_BLOCK_REGISTRY[type]
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() =>
                    addColBlock(pickerMode.slotId, pickerMode.colKey, type)
                  }
                  className="w-full rounded border border-line bg-canvas px-3 py-2 text-left text-xs transition-colors hover:bg-fill"
                >
                  <div className="font-semibold text-fg">{def.label}</div>
                  <div className="mt-0.5 text-fg-secondary">{def.description}</div>
                </button>
              )
            })}
          </div>
        </>
      </div>
      )}

    </aside>
  )
}
